import { fg } from '@atlassian/jira-feature-gating';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';

type TeamsFilterResponse = {
	data: {
		jira: {
			jiraFieldContextualData: {
				edges: Array<{
					node: {
						fieldId: string;
						teamsFilter: string;
					};
				}>;
			};
		};
	};
};

type TeamsFilterForIssueResponse = {
	data: {
		jira: {
			jiraFieldContextualDataForIssue: {
				edges: Array<{
					node: {
						fieldId: string;
						teamsFilter: string;
					};
				}>;
			};
		};
	};
};

const TEAMS_FILTER_QUERY = `
	query GetTeamsFilterForTeamPicker($cloudId: ID!, $filter: JiraFieldContextualDataFilterInput!) {
		jira {
			jiraFieldContextualData(
				cloudId: $cloudId
				first: 1
				filter: $filter
			) {
				edges {
					node {
						... on JiraFieldContextualDataTeamsFilter {
							fieldId
							teamsFilter
						}
					}
				}
			}
		}
	}
`;

const TEAMS_FILTER_FOR_ISSUE_QUERY = `
	query GetTeamsFilterForIssueTeamPicker($cloudId: ID!, $filter: JiraFieldContextualDataForIssueFilterInput!) {
		jira {
			jiraFieldContextualDataForIssue(
				cloudId: $cloudId
				first: 1
				filter: $filter
			) {
				edges {
					node {
						... on JiraFieldContextualDataTeamsFilter {
							fieldId
							teamsFilter
						}
					}
				}
			}
		}
	}
`;

/*
 * @description Fetch the teams filter for a given field ID and cloud ID
 * @param fieldId - The field ID to fetch the teams filter for
 * @param cloudId - The cloud ID to fetch the teams filter for
 * @param projectId - The project ID to scope the filter for (required for the new endpoint)
 * @returns The teams filter for the given field ID and cloud ID (ONLY_VERIFIED or ALL) or null if the teams filter is not found
 */
export const fetchTeamsFilter = async (
	fieldId: string,
	cloudId: string,
	projectId: string,
): Promise<string | null> => {
	try {
		// TODO: Migrate to Relay when this component is migrated
		if (fg('_managed_team_graphql_fix_ken_4267')) {
			// eslint-disable-next-line jira/js/no-jira-fetch
			const response: TeamsFilterForIssueResponse = await performPostRequest(
				'/gateway/api/graphql?operation=teamFilterQuery',
				{
					body: JSON.stringify({
						query: TEAMS_FILTER_FOR_ISSUE_QUERY,
						variables: {
							cloudId,
							filter: {
								fieldIds: [fieldId],
								scopes: [{ projectId }],
							},
						},
					}),
					headers: {
						'content-type': 'application/json',
					},
				},
			);

			return (
				response?.data?.jira?.jiraFieldContextualDataForIssue?.edges?.[0]?.node?.teamsFilter ?? null
			);
		}

		// eslint-disable-next-line jira/js/no-jira-fetch
		const response: TeamsFilterResponse = await performPostRequest(
			'/gateway/api/graphql?operation=teamFilterQuery',
			{
				body: JSON.stringify({
					query: TEAMS_FILTER_QUERY,
					variables: { cloudId, filter: { fieldIds: [fieldId] } },
				}),
				headers: {
					'content-type': 'application/json',
				},
			},
		);

		return response?.data?.jira?.jiraFieldContextualData?.edges?.[0]?.node?.teamsFilter ?? null;
	} catch {
		return null;
	}
};
